import { useEffect, useState } from "react";

import { Button, Toast, Icon, PreviewCardSVG } from "@newsela/angelou";
import { useRouter } from "next/router";

import { FE_USER_ROLES_CONSTANTS } from "@/admin/constants/AdminConsoleUserRolesConstants";
import useAdminCanMakeChanges from "@/admin/hooks/useAdminCanMakeChanges";
import useAdminConsoleAddFacultyPost from "@/admin/hooks/useAdminConsoleAddFacultyPost";
import { DirectoryType } from "@/admin/types";
import { ApiCatalog } from "@/api";
import globalToasterRef from "@/base/globalToasterRef";
import AdminConsoleDirectoryTable from "@/directory/AdminConsoleDirectoryTable";

import { Cell, CellLink, CellLinkList } from "@/directory/DirectoryTable/Cells";
import { NO_FACULTY_MEMBER_RECORD_FOUND } from "@/directory/DirectoryTable/NotFound";
import useGetDirectoryQuery from "@/directory/hooks/useGetDirectoryQuery";
import getAuthProviderName from "@/utils/getAuthProviderName/getAuthProviderName";
import useDirectoryPageAuxiliaryDataContext from "@/admin/useDirectoryPageAuxiliaryDataContext";
import getFullDateFromTimestamp from "../utils/getFullDateFromTimestamp";
import ADMIN_CONSOLE_ROUTE_CONTENTS from "../constants/AdminConsoleRouteContents";
import AdminConsoleRedirectMessageBanner from "../AdminConsoleRedirectMessageBanner/AdminConsoleRedirectMessageBanner";
import AdminConsoleBasePage from "../AdminConsoleBasePage";
import AdminConsoleFacultyAdderModal from "./AdminConsoleFacultyAdderModal";

const PAGE_SIZE = 30;

const FILTER_UI_PROPS = {
  id: "facultyFilters",
  searchFieldLabel: "Search by name",
  searchFieldPlaceholder: "Start typing...",
  title: "Filter faculty",
  triggerButtonLabel: "Filter faculty",
};

const FACULTY_CREATE_ERROR_TOAST_PROPS = {
  showIcon: true,
  statusColor: Toast.statusColor.danger,
  children: "Faculty creation failed",
};

const AdminConsoleFacultyDirectoryPage = () => {
  const router = useRouter();
  const [postFacultyData] = useAdminConsoleAddFacultyPost();
  const [searchValue, setSearchValue] = useState("");
  const [showCantFindBanner, setShowCantFindBanner] = useState(false);
  const canEdit = useAdminCanMakeChanges();
  const [tableControls, setTableControls] = useState();
  const [tableProps, setTableProps] = useState();

  // Initialize items needed to ping the APIs that will source data for the directory pages.
  const {
    userData,
    userDataIsLoading,
    userDataFetchFailed,
    refetchStatusToGenerateCsv,
    responseFromStatusApi,
    refetchingStatusApiResponse,
  } = useDirectoryPageAuxiliaryDataContext();

  const {
    data,
    isLoading,
    refetch,
    isRefetching,
    isError,
    isFetchedAfterMount,
  } = useGetDirectoryQuery({
    user: userData,
    controls: tableControls,
    directoryType: DirectoryType.FACULTY,
    rawTableProps: tableProps,
    apiCatalogEndpoint: ApiCatalog.adminConsoleDirectory.endpoint,
    apiCatalogKey: ApiCatalog.adminConsoleDirectory.key,
    enabled: Boolean(userData) && !userDataIsLoading,
  });

  useEffect(() => {
    if (tableControls) {
      setDisplayErrorMessage(false);
      setShowCantFindBanner(false);
      setLoadingState(true);
      refetch();
      refetchStatusToGenerateCsv();
    }
  }, [tableControls]);

  useEffect(() => {
    // When the page mounts, let's fetch new data if the data
    // is stale, that is, if the existing data was fetched
    // BEFORE the component mounted. That indicates the
    // data was fetched in a prior component's lifecycle.
    if (!isFetchedAfterMount && data) {
      setDisplayErrorMessage(false);
      setLoadingState(true);
      refetch();
      refetchStatusToGenerateCsv();
    }
  }, []);

  const handleAddFacultySubmit = async (facultyAdderState) => {
    try {
      const response = await postFacultyData(facultyAdderState);
      if (response.error) {
        globalToasterRef.current.displayToast(FACULTY_CREATE_ERROR_TOAST_PROPS);
        return;
      }
      if (response.data) {
        sessionStorage.setItem("facultyCreationSuccess", "true");
        const facultyRecordURL = `/admin/faculty/${response.data.id}`;
        router.push(facultyRecordURL);
      }
    } catch {
      globalToasterRef.current.displayToast(FACULTY_CREATE_ERROR_TOAST_PROPS);
    }
  };

  const breadcrumbsSection = {
    href: null,
    contents: ADMIN_CONSOLE_ROUTE_CONTENTS.TEACHERS,
  };

  const transformData = (data = { count: 0, results: [] }) => {
    const { count, results } = data;

    return {
      count,
      faculty: results.map((faculty) => {
        const {
          unique_id: uniqueId,
          name,
          last_login: lastLogin,
          last_login_method: lastLoginMethod,
          role,
          schools,
          classrooms,
          students,
          is_active: isActive,
        } = faculty;

        const capitalizedRoleWithUnderscore =
          role && role.split(" ").join("_").toUpperCase();

        return {
          id: uniqueId,
          name,
          role: FE_USER_ROLES_CONSTANTS[capitalizedRoleWithUnderscore],
          lastLogin: lastLogin ? getFullDateFromTimestamp(lastLogin) : "-",
          schools: schools?.map(({ name }) => name),
          classRooms: classrooms?.map(({ name }) => name),
          students: students?.map(({ name }) => name),
          lastLoginMethod: getAuthProviderName(lastLoginMethod),
          isActive: isActive ? "Active" : "Inactive",
        };
      }),
    };
  };

  const { count, faculty } = transformData(data);

  useEffect(() => {
    if (count) {
      setShowCantFindBanner(count <= 10);
    }
  }, [count]);

  const handleOnCloseBanner = () => {
    setShowCantFindBanner(false);
  };

  const [loadingState, setLoadingState] = useState(true);

  // When we've retrieved all of the required data, hide the loading
  // state.
  useEffect(() => {
    if (!isLoading && !isRefetching && !userDataIsLoading && userData) {
      setLoadingState(false);
    }
  }, [isLoading, isRefetching, userData, userDataIsLoading]);

  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);

  // If there is an error, change the loading state to false and
  // the error state to true.
  useEffect(() => {
    const requiredFetchesFailed = userDataFetchFailed || isError;
    if (requiredFetchesFailed) {
      setDisplayErrorMessage(true);
    }
  }, [userDataFetchFailed, isError]);

  const getDetailsURL = (cell, tab) => {
    const facultyId = cell?.row?.original?.id;
    return tab ? `./${facultyId}?tab=${tab}` : `./${facultyId}/`;
  };

  const columns = [
    {
      accessor: "name",
      Header: "Name",
      Cell: (cell) => {
        const url = getDetailsURL(cell);
        return <CellLink value={cell?.value} href={url} />;
      },
    },
    {
      accessor: "role",
      Header: "Primary role",
      disableSortBy: true,
      Cell: (cell) => <Cell cell={cell} />,
    },
    {
      accessor: "lastLogin",
      Header: "Last login",
      Cell: (cell) => <Cell cell={cell} />,
    },
    {
      accessor: "schools",
      Header: "Schools",
      disableSortBy: true,
      Cell: (cell) => {
        const url = getDetailsURL(cell, "schools");
        return <CellLinkList value={cell.value} label="schools" href={url} />;
      },
    },
    {
      accessor: "classRooms",
      Header: "Classes",
      disableSortBy: true,
      Cell: (cell) => {
        const url = getDetailsURL(cell, "classes");
        return <CellLinkList value={cell.value} label="classes" href={url} />;
      },
    },
    {
      accessor: "students",
      Header: "Students",
      disableSortBy: true,
      Cell: (cell) => {
        const url = getDetailsURL(cell, "students");
        return <CellLinkList value={cell.value} label="students" href={url} />;
      },
    },
    {
      accessor: "isActive",
      Header: "Status",
      disableSortBy: true,
      Cell: (cell) => <Cell cell={cell} />,
    },
    {
      accessor: "lastLoginMethod",
      Header: "Login method",
      disableSortBy: true,
      Cell: (cell) => <Cell cell={cell} />,
    },
    {
      accessor: null,
      Header: "Impersonate",
      disableSortBy: true,
      Cell: (cell) => {
        const { push } = useRouter();
        const [isLoadingImpersonation, setIsLoadingImpersonation] =
          useState(false);
        const goToImpersonation = () => {
          setIsLoadingImpersonation(true);
          push(`/impersonate-start/${cell?.row?.original?.id}`);
        };

        if (cell?.row?.original?.role === FE_USER_ROLES_CONSTANTS.TEACHER) {
          return (
            <Button
              size={Button.sizes.sm}
              hasIcon
              flavor={Button.flavors.secondary}
              onClick={goToImpersonation}
              disabled={isLoadingImpersonation}
            >
              <Icon SvgComponent={PreviewCardSVG} />{" "}
              {isLoadingImpersonation ? "Loading" : "Impersonate"}
            </Button>
          );
        } else {
          return <>Only available for teachers</>;
        }
      },
    },
  ];

  return (
    <AdminConsoleBasePage
      title="Faculty directory"
      breadcrumbsSection={breadcrumbsSection}
      actions={
        canEdit && (
          <AdminConsoleFacultyAdderModal onSubmit={handleAddFacultySubmit} />
        )
      }
      showHeaderSeparator
    >
      {!isLoading && showCantFindBanner && (
        <AdminConsoleRedirectMessageBanner
          linkLabel="student directory"
          linkUrl={`/admin/students/${searchValue ? `?search=${searchValue}` : ""}`}
          onClose={handleOnCloseBanner}
        />
      )}
      <AdminConsoleDirectoryTable
        refetchStatusToGenerateCsv={refetchStatusToGenerateCsv}
        refetchingStatusApiResponse={refetchingStatusApiResponse}
        responseFromStatusApi={responseFromStatusApi}
        userData={userData}
        title="Faculty Directory"
        data={faculty}
        pageSize={PAGE_SIZE}
        totalCount={count}
        columns={columns}
        onControlsUpdateCb={(controls, rawTableProps) => {
          setTableControls(controls);
          setTableProps(rawTableProps);
          setSearchValue(controls?.globalFilter?.searchBoxFilterValue);
        }}
        filterUIProps={FILTER_UI_PROPS}
        loading={loadingState}
        loadingMessage={<div>Loading information...</div>}
        filterDefinitions={{}}
        readQuerySearch
        notFoundMessage={
          <AdminConsoleDirectoryTable.NotFound
            subTitle={NO_FACULTY_MEMBER_RECORD_FOUND}
            littleTopMargin
          />
        }
        shouldDisplayTotalResultsString
        displayErrorMessage={displayErrorMessage && !!loadingState}
      />
    </AdminConsoleBasePage>
  );
};

export default AdminConsoleFacultyDirectoryPage;
